/**
 * CategoryTree — generic, dumb tree-list kit primitive (PIM Wave P1 Task 5,
 * design notes Surface 3; recon C4/C5/A10/§B3). Lives in `_shared/` (not
 * `categories/`) because Task 7's merge target-picker reuses it read-only.
 *
 * Assembles a flat node array (`{id, parentId}` + whatever else the caller's
 * `T` carries) into a nested list purely client-side — there is no admin
 * tree endpoint (recon C4). Sibling order is fully caller-controlled via the
 * `compare` prop (this component has zero opinion on `sort_order`/`name_ru`
 * — the categories feature supplies a comparator that mirrors the backend's
 * `ORDER BY depth, sort_order, name_ru`, recon repository.py:127/1404).
 *
 * Expansion is CONTROLLED (`expandedIds` + `onToggle`) — this component
 * renders a node's children only when `expandedIds.has(node.id)`; it holds
 * no state of its own beyond memoized derived groupings.
 *
 * dnd-kit sibling reorder (PhotoReorderList/OptionsEditor precedent): ONE
 * `DndContext` at the root (sensors + collision detection are global), but a
 * SEPARATE `SortableContext` per parent group (`recon` "separate
 * SortableContext per parent") — `handleDragEnd` additionally guards that
 * the dragged node and the drop target share the same parent group; a
 * cross-group drop is silently ignored (reparenting is out of this
 * component's scope entirely). Reorder is opt-in: omit `onReorderSiblings`
 * (or pass `dragDisabled`) for a read-only tree (Task 7's merge picker).
 *
 * `renderRow` supplies everything AFTER the chevron (name, slug, badges,
 * actions) — this component owns only the chrome: per-depth indent, the
 * optional drag handle, and the expand/collapse chevron (150ms rotate,
 * `aria-expanded`, `motion-reduce` safe).
 */
import {
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import { ChevronRight, GripVertical } from "lucide-react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/** Minimum shape every tree node must carry; `T` may add more. */
export interface CategoryTreeNode {
  id: string;
  parentId: string | null;
}

export interface CategoryTreeRenderMeta {
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
}

export interface CategoryTreeProps<T extends CategoryTreeNode> {
  /** Flat node array — this component does the nesting. */
  nodes: T[];
  /** Controlled expansion set (node id -> children visible). */
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  /** Renders everything after the chevron for one row. */
  renderRow: (node: T, meta: CategoryTreeRenderMeta) => ReactNode;
  /** Sibling sort order — the CALLER owns the actual field mapping. */
  compare: (a: T, b: T) => number;
  /** Fires with the reordered id list for the affected sibling group after
   * a same-parent drag. Omit for a read-only tree (no drag handle at all). */
  onReorderSiblings?: (parentId: string | null, orderedIds: string[]) => void;
  /** Force-disables the drag handle even when `onReorderSiblings` is set
   * (e.g. while a search filter is active). */
  dragDisabled?: boolean;
  /** aria-label for a row's expand/collapse chevron. */
  getToggleLabel: (node: T, expanded: boolean) => string;
  /** aria-label for a row's drag handle. */
  getDragHandleLabel: (node: T, index: number) => string;
  /** `data-testid` prefix; rows/handles/toggles derive from it. */
  testid?: string;
}

const ROW_CHROME_BUTTON_CLASS =
  "grid size-7 shrink-0 place-items-center rounded-[var(--g-radius-sm)] text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-40";

interface RowProps<T extends CategoryTreeNode> {
  node: T;
  index: number;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  reorderEnabled: boolean;
  onToggle: (id: string) => void;
  renderRow: CategoryTreeProps<T>["renderRow"];
  getToggleLabel: CategoryTreeProps<T>["getToggleLabel"];
  getDragHandleLabel: CategoryTreeProps<T>["getDragHandleLabel"];
  testid: string;
  children: ReactNode;
}

function CategoryTreeRow<T extends CategoryTreeNode>({
  node,
  index,
  depth,
  hasChildren,
  isExpanded,
  reorderEnabled,
  onToggle,
  renderRow,
  getToggleLabel,
  getDragHandleLabel,
  testid,
  children,
}: RowProps<T>): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled: !reorderEnabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`${testid}-row`}
      data-depth={depth}
      className="motion-reduce:!transition-none"
    >
      <div
        className="flex items-center gap-1 py-0.5"
        style={depth > 0 ? { marginLeft: depth * 20 } : undefined}
      >
        {reorderEnabled ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={getDragHandleLabel(node, index)}
            data-testid={`${testid}-drag-${node.id}`}
            className={`${ROW_CHROME_BUTTON_CLASS} cursor-grab`}
          >
            <GripVertical size={16} strokeWidth={1.75} aria-hidden />
          </button>
        ) : (
          <span className="inline-block size-7 shrink-0" aria-hidden />
        )}

        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            aria-expanded={isExpanded}
            aria-label={getToggleLabel(node, isExpanded)}
            data-testid={`${testid}-toggle-${node.id}`}
            className={ROW_CHROME_BUTTON_CLASS}
          >
            <ChevronRight
              size={16}
              strokeWidth={1.75}
              aria-hidden
              className={[
                "transition-transform duration-150 motion-reduce:transition-none",
                isExpanded ? "rotate-90" : "",
              ].join(" ")}
            />
          </button>
        ) : (
          <span className="inline-block size-7 shrink-0" aria-hidden />
        )}

        <div className="min-w-0 flex-1">{renderRow(node, { depth, hasChildren, isExpanded })}</div>
      </div>

      {hasChildren && isExpanded ? children : null}
    </li>
  );
}

interface LevelProps<T extends CategoryTreeNode> {
  parentId: string | null;
  depth: number;
  childrenByParent: Map<string | null, T[]>;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  renderRow: CategoryTreeProps<T>["renderRow"];
  reorderEnabled: boolean;
  getToggleLabel: CategoryTreeProps<T>["getToggleLabel"];
  getDragHandleLabel: CategoryTreeProps<T>["getDragHandleLabel"];
  testid: string;
}

function CategoryTreeLevel<T extends CategoryTreeNode>({
  parentId,
  depth,
  childrenByParent,
  expandedIds,
  onToggle,
  renderRow,
  reorderEnabled,
  getToggleLabel,
  getDragHandleLabel,
  testid,
}: LevelProps<T>): ReactElement | null {
  const items = childrenByParent.get(parentId) ?? [];
  if (items.length === 0) return null;

  return (
    <SortableContext items={items.map((n) => n.id)} strategy={verticalListSortingStrategy}>
      <ul
        className={depth > 0 ? "ml-4 flex flex-col border-l border-[var(--g-hairline)] pl-1" : "flex flex-col"}
        data-testid={depth === 0 ? `${testid}-roots` : undefined}
      >
        {items.map((node, index) => {
          const hasChildren = (childrenByParent.get(node.id)?.length ?? 0) > 0;
          const isExpanded = expandedIds.has(node.id);
          return (
            <CategoryTreeRow
              key={node.id}
              node={node}
              index={index}
              depth={depth}
              hasChildren={hasChildren}
              isExpanded={isExpanded}
              reorderEnabled={reorderEnabled}
              onToggle={onToggle}
              renderRow={renderRow}
              getToggleLabel={getToggleLabel}
              getDragHandleLabel={getDragHandleLabel}
              testid={testid}
            >
              <CategoryTreeLevel
                parentId={node.id}
                depth={depth + 1}
                childrenByParent={childrenByParent}
                expandedIds={expandedIds}
                onToggle={onToggle}
                renderRow={renderRow}
                reorderEnabled={reorderEnabled}
                getToggleLabel={getToggleLabel}
                getDragHandleLabel={getDragHandleLabel}
                testid={testid}
              />
            </CategoryTreeRow>
          );
        })}
      </ul>
    </SortableContext>
  );
}

export function CategoryTree<T extends CategoryTreeNode>({
  nodes,
  expandedIds,
  onToggle,
  renderRow,
  compare,
  onReorderSiblings,
  dragDisabled = false,
  getToggleLabel,
  getDragHandleLabel,
  testid = "category-tree",
}: CategoryTreeProps<T>): ReactElement {
  const nodesById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // Group by parent, falling back to a root-level group for any node whose
  // declared parent isn't present in this node set — defensive against a
  // filtered-down node list (search) ever producing an orphan; mirrors the
  // backend's own orphan-promotion philosophy (`public_router._assemble_tree`).
  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, T[]>();
    for (const n of nodes) {
      const key = n.parentId !== null && nodesById.has(n.parentId) ? n.parentId : null;
      const list = map.get(key);
      if (list) list.push(n);
      else map.set(key, [n]);
    }
    for (const list of map.values()) list.sort(compare);
    return map;
  }, [nodes, nodesById, compare]);

  const reorderEnabled = !dragDisabled && typeof onReorderSiblings === "function";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    if (!reorderEnabled || !onReorderSiblings) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeNode = nodesById.get(String(active.id));
    const overNode = nodesById.get(String(over.id));
    if (!activeNode || !overNode) return;

    const parentKeyOf = (n: T): string | null =>
      n.parentId !== null && nodesById.has(n.parentId) ? n.parentId : null;
    const activeParentKey = parentKeyOf(activeNode);
    if (activeParentKey !== parentKeyOf(overNode)) return; // cross-group drop — ignore

    const siblings = childrenByParent.get(activeParentKey) ?? [];
    const oldIndex = siblings.findIndex((n) => n.id === activeNode.id);
    const newIndex = siblings.findIndex((n) => n.id === overNode.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(siblings, oldIndex, newIndex);
    onReorderSiblings(activeParentKey, reordered.map((n) => n.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <CategoryTreeLevel
        parentId={null}
        depth={0}
        childrenByParent={childrenByParent}
        expandedIds={expandedIds}
        onToggle={onToggle}
        renderRow={renderRow}
        reorderEnabled={reorderEnabled}
        getToggleLabel={getToggleLabel}
        getDragHandleLabel={getDragHandleLabel}
        testid={testid}
      />
    </DndContext>
  );
}

export default CategoryTree;
